import type { ReactNode, Ref } from "react";
import { useEffect, useState } from "react";

import { cn } from "../lib/utils";

/**
 * Outer size of the device shell in CSS px — the 402x874 screen plus the 8px
 * black bezel and the 8px halo (`p-2`) around it. Drives the scale-to-fit
 * factor below.
 */
const FRAME_OUTER_WIDTH = 434;
const FRAME_OUTER_HEIGHT = 906;

/** Breathing room kept between the scaled shell and the viewport edges. */
const FIT_MARGIN = 32;

/** Tailwind `md:` breakpoint — below this no device shell is drawn. */
const DESKTOP_MIN_WIDTH = 768;

/**
 * Factor the device shell must shrink by to fit entirely inside the viewport,
 * capped at 1 so the shell is never blown up past its design size. Always 1 at
 * mobile widths, where the shell is not rendered.
 *
 * At 1440x900 the shell would otherwise overflow (906px tall + browser chrome),
 * which is the window size demo recordings are captured at.
 */
const useFitScale = (enabled: boolean): number => {
  const [scale, setScale] = useState(1);

  useEffect(() => {
    if (!enabled) {
      setScale(1);
      return;
    }
    const update = () => {
      if (window.innerWidth < DESKTOP_MIN_WIDTH) {
        setScale(1);
        return;
      }
      const next = Math.min(
        1,
        (window.innerHeight - FIT_MARGIN * 2) / FRAME_OUTER_HEIGHT,
        (window.innerWidth - FIT_MARGIN * 2) / FRAME_OUTER_WIDTH
      );
      setScale(next > 0 ? next : 1);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [enabled]);

  return scale;
};

interface MobileFrameProps {
  children: ReactNode;
  /** URL shown in the bottom Safari address bar. Default: "faire.com". */
  url?: string;
  /** Background behind the device shell on desktop. Default: light grey. */
  desktopBackground?: string;
  /**
   * Optional handler invoked when the Safari "back" chevron is clicked. Useful
   * for hiding a reset/restart action behind the back button so designers can
   * loop through a multi-step prototype without reloading the page.
   */
  onBack?: () => void;
  /**
   * Optional content rendered pinned above the Safari toolbar on desktop, and
   * flush at the bottom of the children stack on mobile. Use for things like a
   * FAIRE wordmark or footer copy that should stay anchored regardless of
   * content height.
   */
  footer?: ReactNode;
  /**
   * Optional content rendered fixed-positioned behind the iPhone shell on
   * desktop. Use for ambient backgrounds (blurred photos, gradients) that
   * should fill the area around the phone. Always pointer-events: none and
   * desktop-only — mobile keeps the white prototype background.
   */
  backdrop?: ReactNode;
  /**
   * When true, removes the rounded gray buffer that surrounds the iPhone
   * shell on desktop. The phone sits directly on the page background, with
   * no padding/rounding/fill between shell and surroundings. Useful for
   * thumbnails and marketing shots where the phone should pop against the
   * page background. Defaults to false.
   */
  desktopFrameless?: boolean;
  /**
   * When true, hides the Safari floating toolbar at the bottom of the
   * desktop iPhone shell. Use for prototypes that mimic native iOS apps
   * (e.g. Apple Wallet, Mail) where Safari chrome would be incorrect.
   * Defaults to false.
   */
  hideSafariBar?: boolean;
  /**
   * Flat colour filling the whole desktop canvas behind the device shell.
   * Default: white. Set this and `desktopBackground` to the same value for an
   * edge-to-edge flat backdrop, which reads better in a recorded demo than the
   * default two-tone treatment. Ignored when `backdrop` is set.
   */
  canvasBackground?: string;
  /**
   * Shrink the device shell so it fits entirely within the viewport, and pin
   * the canvas to the viewport height so nothing scrolls behind it. The shell
   * is never scaled above 1.
   *
   * Off by default: this changes the page's layout, and `MobileFrame` is
   * shared, so existing prototypes keep their original scrolling behaviour
   * unless they ask for it.
   */
  scaleToFit?: boolean;
  /**
   * Soft drop shadow under the device shell. On by default; turn it off when
   * recording a GIF — a large soft gradient burns most of a 256-colour palette
   * and shows up as banding.
   */
  showShadow?: boolean;
  /**
   * Content layered over the whole device screen, inside the rounded clip —
   * for a simulated software keyboard, a touch pointer, and the like. Desktop
   * shell only, and `pointer-events-none`.
   */
  overlay?: ReactNode;
  /**
   * Ref to the device screen element (the 402x874 area inside the bezel).
   * Useful for measuring positions of things drawn into `overlay`.
   */
  screenRef?: Ref<HTMLDivElement>;
}

/**
 * Wraps prototype content in a faux iPhone + iOS Safari shell on desktop, so a
 * mobile-web prototype can be previewed in context on a wider screen.
 *
 * On viewports `<768px` (Tailwind `md:` breakpoint) the shell is hidden and the
 * children fill the viewport — meaning the same prototype can be opened on a
 * real phone and look like a normal mobile-web page.
 *
 * Inner viewport on desktop matches the Figma spec: 375px wide × ~772px tall,
 * scrollable inside an iPhone Pro outline (402×874, 8px black border, 73.5px
 * corner radius).
 */
export const MobileFrame: React.FC<MobileFrameProps> = ({
  children,
  url = "faire.com",
  desktopBackground = "#d8d8d8",
  onBack,
  footer,
  backdrop,
  desktopFrameless = false,
  hideSafariBar = false,
  canvasBackground = "#ffffff",
  scaleToFit = false,
  showShadow = true,
  overlay,
  screenRef,
}) => {
  const scale = useFitScale(scaleToFit);

  return (
    <>
      {backdrop ? (
        <div
          aria-hidden
          className="pointer-events-none fixed inset-0 z-0 hidden overflow-hidden md:block"
        >
          {backdrop}
        </div>
      ) : null}
      <div
        className={cn(
          "relative min-h-screen md:flex md:items-center md:justify-center",
          // Scaled: pin to the viewport so the shell can never overflow. Unscaled:
          // keep the original padded, scrollable canvas.
          scaleToFit ? "md:h-screen md:overflow-hidden" : "md:py-12",
          backdrop
            ? "bg-fs-surface-primary md:bg-transparent"
            : "bg-fs-surface-primary md:bg-[var(--mobile-frame-canvas-bg)]"
        )}
        style={{
          ["--mobile-frame-bg" as string]: desktopBackground,
          ["--mobile-frame-canvas-bg" as string]: canvasBackground,
        }}
      >
        <div
          className={cn(
            !desktopFrameless &&
              "md:rounded-[80px] md:bg-[var(--mobile-frame-bg)] md:p-2"
          )}
          style={
            scale === 1
              ? undefined
              : { transform: `scale(${scale})`, transformOrigin: "center" }
          }
        >
          <div
            ref={screenRef}
            className={cn(
              "md:bg-fs-surface-primary relative md:h-[874px] md:w-[402px] md:cursor-grab md:overflow-hidden md:rounded-[73.5px] md:border-[8px] md:border-(--slate-color-grey-1000)",
              showShadow && "md:shadow-[0_30px_60px_-15px_rgba(0,0,0,0.25)]"
            )}
          >
            {/* Status bar (desktop frame only). Sized + positioned to match the
            Dynamic Island so the time on the left and the signal/wifi/battery
            icons on the right stay vertically centered with the Island. */}
            <div className="pointer-events-none absolute inset-x-0 top-[14px] z-20 hidden h-[36px] items-center px-[35px] md:flex">
              <span className="flex-1 text-[17px] font-semibold tracking-[-0.4px] text-(--slate-color-grey-1000) tabular-nums">
                9:41
              </span>
              <div className="flex items-center gap-[7px]">
                <CellularIcon />
                <WifiIcon />
                <BatteryIcon />
              </div>
            </div>

            {/* Dynamic Island */}
            <div className="pointer-events-none absolute top-[14px] left-1/2 z-30 hidden h-[36px] w-[124px] -translate-x-1/2 rounded-[18px] bg-(--slate-color-grey-1000) md:block" />

            {/* Inner mobile viewport — full content area on desktop, full bleed
            on mobile. On desktop, top-[64px] keeps content clear of the
            status bar + Dynamic Island. Overflow is `visible` (NOT auto) so
            page elements can use full-bleed positioning to reach the iPhone
            shell's border edges; per CSS spec, `overflow-y: auto` would
            coerce `overflow-x` to `auto` and clip any horizontal extension.
            Pages that need vertical scrolling implement it on a child
            element instead, leaving full-bleed elements free to extend. */}
            <div className="md:absolute md:top-[64px] md:right-[13.5px] md:bottom-0 md:left-[13.5px] md:overflow-visible">
              {children}
            </div>

            {/* Pinned footer slot — sits above the Safari toolbar on desktop and
            renders inline at the bottom on mobile. */}
            {footer ? (
              <div className="z-10 flex justify-center px-[16px] py-[16px] md:pointer-events-none md:absolute md:right-0 md:bottom-[100px] md:left-0">
                <div className="md:pointer-events-auto">{footer}</div>
              </div>
            ) : null}

            {/* Safari floating toolbar (desktop frame only) */}
            {hideSafariBar ? null : (
              <div className="absolute bottom-[34px] left-1/2 z-20 hidden -translate-x-1/2 items-center gap-[10px] md:flex">
                <SafariPillButton ariaLabel="Back" onClick={onBack}>
                  <BackChevronIcon />
                </SafariPillButton>
                <div className="bg-fs-surface-primary/70 relative flex h-[48px] w-[218px] items-center justify-center rounded-[24px] border border-(--slate-color-grey-100) shadow-[0_2px_42px_0_rgba(0,0,0,0.1)] backdrop-blur-md">
                  <SiteSettingsIcon />
                  <span className="text-[17px] font-medium tracking-[-0.4px] text-[#1b1b1b]">
                    {url}
                  </span>
                  <ReloadIcon />
                </div>
                <SafariPillButton ariaLabel="More">
                  <EllipsisIcon />
                </SafariPillButton>
              </div>
            )}

            {/* Overlay slot — keyboard, touch pointer, and anything else drawn
            over the whole screen. Desktop shell only. */}
            {overlay ? (
              <div className="pointer-events-none absolute inset-0 z-[35] hidden md:block">
                {overlay}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
};

interface SafariPillButtonProps {
  children: ReactNode;
  ariaLabel: string;
  onClick?: () => void;
}

const SafariPillButton: React.FC<SafariPillButtonProps> = ({
  children,
  ariaLabel,
  onClick,
}) => {
  const className =
    "flex items-center justify-center w-[48px] h-[48px] rounded-[24px] border border-(--slate-color-grey-100) bg-fs-surface-primary/70 backdrop-blur-md shadow-[0_2px_40px_0_rgba(0,0,0,0.1)]";
  if (onClick) {
    return (
      <button
        type="button"
        aria-label={ariaLabel}
        onClick={onClick}
        className={`${className} cursor-pointer`}
      >
        {children}
      </button>
    );
  }
  return (
    <div role="img" aria-label={ariaLabel} className={className}>
      {children}
    </div>
  );
};

const BackChevronIcon: React.FC = () => (
  <svg width="10" height="18" viewBox="0 0 10 18" fill="none" aria-hidden>
    <path
      d="M9 1L2 9L9 17"
      stroke="#1b1b1b"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const EllipsisIcon: React.FC = () => (
  <svg width="20" height="6" viewBox="0 0 20 6" fill="#1b1b1b" aria-hidden>
    <circle cx="3" cy="3" r="2.2" />
    <circle cx="10" cy="3" r="2.2" />
    <circle cx="17" cy="3" r="2.2" />
  </svg>
);

const SiteSettingsIcon: React.FC = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 15 15"
    fill="none"
    aria-hidden
    className="absolute top-1/2 left-[14px] -translate-y-1/2"
  >
    <rect
      x="0.5"
      y="2.5"
      width="14"
      height="10"
      rx="2"
      stroke="#1b1b1b"
      strokeWidth="1.4"
    />
    <line x1="0.5" y1="6" x2="14.5" y2="6" stroke="#1b1b1b" strokeWidth="1.4" />
  </svg>
);

const ReloadIcon: React.FC = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 15 15"
    fill="none"
    aria-hidden
    className="absolute top-1/2 right-[12px] -translate-y-1/2"
  >
    <path
      d="M2 7.5a5.5 5.5 0 0 1 9.4-3.9M13 7.5a5.5 5.5 0 0 1-9.4 3.9"
      stroke="#1b1b1b"
      strokeWidth="1.4"
      strokeLinecap="round"
    />
    <path
      d="M11.5 1.5v3h-3"
      stroke="#1b1b1b"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3.5 13.5v-3h3"
      stroke="#1b1b1b"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CellularIcon: React.FC = () => (
  <svg
    width="18"
    height="12"
    viewBox="0 0 18 12"
    fill="#000"
    aria-label="Signal"
  >
    <rect x="0" y="8" width="3" height="4" rx="0.5" />
    <rect x="5" y="5" width="3" height="7" rx="0.5" />
    <rect x="10" y="2" width="3" height="10" rx="0.5" />
    <rect x="15" y="0" width="3" height="12" rx="0.5" />
  </svg>
);

const WifiIcon: React.FC = () => (
  <svg
    width="16"
    height="12"
    viewBox="0 0 16 12"
    fill="#000"
    aria-label="Wi-Fi"
  >
    <path d="M8 0a13 13 0 0 1 8 2.7l-1.5 1.8A10.6 10.6 0 0 0 8 2.4 10.6 10.6 0 0 0 1.5 4.5L0 2.7A13 13 0 0 1 8 0Z" />
    <path d="M8 4a8.6 8.6 0 0 1 5.3 1.8l-1.5 1.8a6.2 6.2 0 0 0-7.6 0L2.7 5.8A8.6 8.6 0 0 1 8 4Z" />
    <path d="M8 8a4 4 0 0 1 2.5.9L8 12 5.5 8.9A4 4 0 0 1 8 8Z" />
  </svg>
);

const BatteryIcon: React.FC = () => (
  <svg
    width="26"
    height="12"
    viewBox="0 0 26 12"
    fill="none"
    aria-label="Battery"
  >
    <rect
      x="0.5"
      y="0.5"
      width="22"
      height="11"
      rx="2.5"
      stroke="#000"
      opacity="0.35"
    />
    <rect x="2" y="2" width="19" height="8" rx="1.5" fill="#000" />
    <rect
      x="23.5"
      y="4"
      width="2"
      height="4"
      rx="1"
      fill="#000"
      opacity="0.4"
    />
  </svg>
);
